import { Injectable } from '@angular/core';
import { Business, BusinessAddress, BusinessUser } from '@models/businesses';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { cloneDeep } from 'lodash';
import { BaseFormMapper } from '../base-form-mapper';
import {
	Matter,
	PersonMatterClient,
	BusinessMatterClient,
	PersonMatterClientWithAssociations,
	BusinessMatterClientWithAssociations,
	CalculatedLandTransferTax,
	ClientsCapacity,
	MatterTitle,
	MatterFileType,
} from '@models/matter';
import { LawFirm } from '@models/lawFirms';
import { format } from 'date-fns';
import { addressString } from '@core/util/address';
import { clientLegalName } from '@core/util/client';
import { MatterMortgage } from '@models/mortgage';
import { isSet } from '@core/util/value';
import { getPrimaryParcel } from '@core/util/parcels';
import { formatCentsToDollars } from '@core/util/money/centsToDollars';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { getAreaCode, getPrefixAndLineNumber } from '@core/util/phone';
import {
	CorrespondenceType,
	FinancialInstitutionLender,
	LLCFinancialInstitutionLender,
} from '@models/lender';
import { joinNames } from '@core/util/string';
import { getClientBrokerageFromMatter } from '@state/utils/helper';

@Injectable({
	providedIn: 'root',
})
export class FCT_OVER_25_Mapper extends BaseFormMapper<FCT_OVER_25> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: FCT_OVER_25,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
		matterTitles: MatterTitle[],
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		lttCalculated: CalculatedLandTransferTax,
		businessAddress: BusinessAddress,
		otherParties: PersonMatterClient[],
		clientsCapacity: ClientsCapacity,
		mortgage: MatterMortgage,
		businessUsers: BusinessUser[],
	): FCT_OVER_25 {
		const _formData = formData ? cloneDeep(formData) : ({} as FCT_OVER_25);

		const primaryLender = mortgage?.lenders
			? Object.values(mortgage.lenders).find((lender) => lender.primary)
			: undefined;

		const correspondence = mortgage?.correspondence;

		if (matter?.number && !isSet(_formData?.yourFileNumber)) {
			_formData.yourFileNumber = matter?.number;
		}

		if (!isSet(_formData?.date)) {
			_formData.date = format(new Date(), 'MMMM dd, yyyy');
		}

		if (matter?.lawyerAssigned?.name && !isSet(_formData?.solicitorNotary)) {
			_formData.solicitorNotary = matter.lawyerAssigned.name;
		}

		if (matter?.clerkAssigned?.name && !isSet(_formData?.contact)) {
			_formData.contact = matter.clerkAssigned.name;
		}

		if (business?.name && !isSet(_formData?.lawFirm)) {
			_formData.lawFirm = business.name;
		}

		if (businessAddress && !isSet(_formData?.addressNewCustomersOnly)) {
			_formData.addressNewCustomersOnly = addressString(businessAddress);
		}

		// TODO: CLERK STUFF

		const clerkAssigned = businessUsers?.find(
			(user) => user.id === matter.clerkAssigned?.id,
		);
		if (clerkAssigned?.email && !isSet(_formData?.emailAddress)) {
			_formData.emailAddress = clerkAssigned.email;
		}

		if (
			clerkAssigned?.phoneNumber &&
			!isSet(_formData?.telephoneNumberAreaCode)
		) {
			_formData.telephoneNumberAreaCode = getAreaCode(
				clerkAssigned.phoneNumber,
			);
		}

		if (clerkAssigned?.phoneNumber && !isSet(_formData?.telephoneNumber)) {
			_formData.telephoneNumber = getPrefixAndLineNumber(
				clerkAssigned.phoneNumber,
			);
		}

		if (clerkAssigned?.faxNumber && !isSet(_formData?.faxNumberAreaCode)) {
			_formData.faxNumberAreaCode = getAreaCode(clerkAssigned.faxNumber);
		}

		if (clerkAssigned?.faxNumber && !isSet(_formData?.faxNumber)) {
			_formData.faxNumber = getPrefixAndLineNumber(clerkAssigned.faxNumber);
		}

		if (correspondence?.correspondenceType === CorrespondenceType.SOLICITOR) {
			const solicitor = correspondence?.correspondence as LawFirm;
			if (solicitor?.lawyer && !isSet(_formData?.solicitorNotary1)) {
				_formData.solicitorNotary1 = [
					solicitor.lawyer.firstName,
					solicitor.lawyer.lastName,
				].join(' ');
			}

			if (solicitor?.clerk && !isSet(_formData?.contact1)) {
				_formData.contact1 = [
					solicitor.clerk.firstName,
					solicitor.clerk.lastName,
				].join(' ');
			}

			if (solicitor?.name && !isSet(_formData?.lawFirm1)) {
				_formData.lawFirm1 = solicitor.name;
			}

			if (solicitor?.address && !isSet(_formData?.address)) {
				_formData.address = addressString(solicitor.address);
			}

			if (solicitor?.email && !isSet(_formData?.emailAddress1)) {
				_formData.emailAddress1 = solicitor.email;
			}

			if (
				solicitor?.phoneNumber &&
				!isSet(_formData?.telephoneNumberAreaCode1)
			) {
				_formData.telephoneNumberAreaCode1 = getAreaCode(solicitor.phoneNumber);
			}

			if (solicitor?.phoneNumber && !isSet(_formData?.telephoneNumber1)) {
				_formData.telephoneNumber1 = getPrefixAndLineNumber(
					solicitor.phoneNumber,
				);
			}

			if (solicitor?.faxNumber && !isSet(_formData?.faxNumberAreaCode1)) {
				_formData.faxNumberAreaCode1 = getAreaCode(solicitor.faxNumber);
			}

			if (solicitor?.faxNumber && !isSet(_formData?.faxNumber1)) {
				_formData.faxNumber1 = getPrefixAndLineNumber(solicitor.faxNumber);
			}
		}

		if (matter?.closingDate && !isSet(_formData?.closingDateMmDdYyyy)) {
			_formData.closingDateMmDdYyyy = format(
				new Date(matter.closingDate),
				'MM/dd/yyyy',
			);
		}

		if (
			matter?.fileType === MatterFileType.PURCHASE &&
			!isSet(_formData?.purchasePrice)
		) {
			_formData.purchasePrice = formatCentsToDollars(
				matter.purchasePrice,
				false,
			);
		}

		if (
			matter?.propertyDetails?.address &&
			!isSet(_formData?.municipalAddressLine1)
		) {
			_formData.municipalAddressLine1 = addressString(
				matter.propertyDetails.address,
			);
		}

		const primaryParcel = getPrimaryParcel(matterTitles);

		if (primaryParcel?.pin && !isSet(_formData?.pin)) {
			_formData.pin = primaryParcel.pin;
		}

		if (
			primaryParcel?.description &&
			!isSet(_formData?.legalDescriptionLine1)
		) {
			_formData.legalDescriptionLine1 = primaryParcel.description;
		}

		if (matterClients?.length && !isSet(_formData?.purchaserMortgagorName)) {
			_formData.purchaserMortgagorName = joinNames(
				matterClients.map((mc) => clientLegalName(mc, nameFormattingSettings)),
			);
		}

		const primaryClient =
			matterClients?.find((mc) => mc.primaryContact) ?? null;

		if (primaryClient && !isSet(_formData?.purchaserAddressForService)) {
			_formData.purchaserAddressForService = addressString(
				primaryClient.serviceAddress,
			);
		}

		if (
			((primaryLender as LLCFinancialInstitutionLender)?.llcMortgagee?.nameEn ||
				(primaryLender as FinancialInstitutionLender)?.name) &&
			!isSet(_formData?.mortgagee)
		) {
			_formData.mortgagee =
				primaryLender?.llcMortgagee?.nameEn ?? primaryLender?.name;
		}

		if (
			primaryLender?.address &&
			!isSet(_formData?.mortgageeAddressForService)
		) {
			_formData.mortgageeAddressForService = addressString(
				primaryLender?.address,
			);
		}

		if (mortgage?.amount && !isSet(_formData?.mortgageAmount)) {
			_formData.mortgageAmount = formatCentsToDollars(mortgage.amount, false);
		}

		if (mortgage?.amount && !isSet(_formData?.insuredAmount)) {
			_formData.insuredAmount = formatCentsToDollars(mortgage.amount, false);
		}

		if (
			mortgage?.referenceNumber &&
			!isSet(_formData?.mortgageReferenceNumber)
		) {
			_formData.mortgageReferenceNumber = mortgage.referenceNumber;
		}

		const clientBrokerage = getClientBrokerageFromMatter(matter);

		if (clientBrokerage?.name && !isSet(_formData?.companyAgentName)) {
			_formData.companyAgentName = clientBrokerage.name;
		}

		if (
			clientBrokerage?.phoneNumber &&
			!isSet(_formData?.telephoneNumberAreaCode2)
		) {
			_formData.telephoneNumberAreaCode2 = getAreaCode(
				clientBrokerage.phoneNumber,
			);
		}

		if (clientBrokerage?.phoneNumber && !isSet(_formData?.telephoneNumber2)) {
			_formData.telephoneNumber2 = getPrefixAndLineNumber(
				clientBrokerage.phoneNumber,
			);
		}

		if (
			matter?.fileType === MatterFileType.PURCHASE &&
			(matter?.otherPartyLawFirm?.name ||
				matter?.otherPartyLawFirm?.lawyer?.firstName) &&
			!isSet(_formData?.nameOfVendorsSolicitorFirmAndLawyerName)
		) {
			_formData.nameOfVendorsSolicitorFirmAndLawyerName = [
				matter.otherPartyLawFirm.name,
				matter.otherPartyLawFirm.lawyer.firstName +
					' ' +
					matter.otherPartyLawFirm.lawyer.lastName,
			].join(' ');
		}

		if (
			matter?.fileType === MatterFileType.PURCHASE &&
			matter?.otherPartyLawFirm?.phoneNumber &&
			!isSet(_formData?.phoneNumber)
		) {
			_formData.phoneNumber = matter.otherPartyLawFirm.phoneNumber;
		}

		return _formData;
	}

	MapValues(): Partial<FCT_OVER_25> {
		return {};
	}
}

const Mappings: FCT_OVER_25 = {
	transactionInformation: 'Transaction Information',
	interestHeld: 'Interest Held:',
	propertyType: 'Property Type:',
	hasAnOrderForThisTransactionPreviouslyBeenPlacedWithAnotherTitleInsurer:
		'Has an order for this transaction previously been placed with another title insurer?',
	yourFileNumber: 'Your File Number',
	date: 'Date:',
	solicitorNotary: 'Solicitor/Notary:',
	contact: 'Contact:',
	lawFirm: 'Law Firm:',
	addressNewCustomersOnly: 'Address: (new customers only)',
	emailAddress: 'Email Address:',
	telephoneNumberAreaCode: 'Telephone Number Area Code',
	telephoneNumber: 'Telephone Number',
	faxNumberAreaCode: 'Fax Number Area Code',
	faxNumber: 'Fax Number',
	solicitorNotary1: 'Solicitor/Notary: 1',
	contact1: 'Contact: 1',
	lawFirm1: 'Law Firm: 1',
	address: 'Address:',
	emailAddress1: 'Email Address: 1',
	telephoneNumberAreaCode1: 'Telephone Number Area Code 1',
	telephoneNumber1: 'Telephone Number 1',
	faxNumberAreaCode1: 'Fax Number Area Code 1',
	faxNumber1: 'Fax Number 1',
	fctReferenceNumber: 'FCT Reference Number',
	closingDateMmDdYyyy: 'Closing Date: MM/DD/YYYY',
	purchasePrice: 'Purchase Price: $',
	numberOfUnitsIfCmhcInsured: 'Number of Units (if CMHC insured):',
	otherPleaseSpecify: 'Other (please specify)',
	ifLeaseholdPleaseProvideNameOfLandlordAndLeaseRegistrationParticulars:
		'If Leasehold, please provide name of Landlord and Lease registration particulars',
	pleaseProvideTermOfLease: 'Please provide term of lease',
	municipalAddressLine1: 'Municipal Address: Line 1',
	municipalAddressLine2: 'Municipal Address: Line 2',
	legalVehicularAndPedestrianAccessToThePropertyIsVia:
		'Legal vehicular and pedestrian access to the property is via:',
	legalDescriptionLine1: 'Legal Description: Line 1',
	legalDescriptionLine2: 'Legal Description: Line 2',
	isThePropertyContiguous: 'Is the property contiguous?',
	doesTheLegalDescriptionDescribeTheSamePropertyAsThatIdentifiedByTheAssessmentRollNumber:
		'Does the legal description describe the same property as that identified by the assessment roll number?',
	municipalAddress1Line1: 'Municipal Address: 1 Line 1',
	municipalAddress1Line2: 'Municipal Address: 1 Line 2',
	legalDescription1Line1: 'Legal Description: 1 Line 1',
	legalDescription1Line2: 'Legal Description: 1 Line 2',
	purchaserMortgagorName: 'Purchaser/Mortgagor Name:',
	beneficialOwnerIfApplicable: 'Beneficial Owner (if applicable):',
	hasTheBeneficialOwnerConsentedToThisTransaction:
		'Has the Beneficial Owner consented to this transaction?',
	areYouActingForThePurchaserMortgagor:
		'Are you acting for the Purchaser/Mortgagor?',
	ifMortgagorIsACorporationHasANoticeOfChangeBeenFiledWithinThePast12MonthsThatChangesTheOfficersDirectorsOrShareholders:
		'If mortgagor is a Corporation has a notice of change been filed within the past 12 months that changes the Officers, Directors or Shareholders?',
	mortgagee: 'Mortgagee:',
	custodianIfApplicable: 'Custodian (if applicable):',
	mortgageAmount: 'Mortgage Amount: $',
	insuredAmount: 'Insured Amount: $',
	mortgageReferenceNumber: 'Mortgage Reference Number',
	priority: 'Priority:',
	other: 'Other',
	wouldYouLikeToAddTheExtendedSuperPriorityLiensCoverageAdditionalPremiumApplies:
		'Would you like to add the extended Super Priority Liens coverage? (additional premium applies)',
	wouldYouLikeToAddThePostPolicyDateEncroachmentsRestrictionsWorkOrdersCoverageAdditionalPremiumApplies:
		'Would you like to add the Post-Policy Date - Encroachments, Restrictions & Work Orders coverage? (additional premium applies)',
	pin: 'P.I.N.:',
	pin1: 'P.I.N.: 1',
	legalDescription: 'Legal Description:',
	legalVehicularAndPedestrianAccessToThePropertyIsVia1:
		'Legal vehicular and pedestrian access to the property is via: 1',
	unknown: 'Unknown',
	unknown1: 'Unknown 1',
	legalDescription1: 'Legal Description: 1',
	isThePropertyContiguous1: 'Is the property contiguous? 1',
	ifNoPleaseProvideDetails: 'If no, please provide details:',
	doesTheLegalDescriptionDescribeTheSamePropertyAsThatIdentifiedByTheAssessmentRollNumber1:
		'Does the legal description describe the same property as that identified by the assessment roll number? 1',
	doesTheLegalDescriptionContainAnyReferenceToTogetherWithRightsOfWayOrEasements:
		'Does the legal description contain any reference to together with rights of way or easements?',
	doesTheLegalDescriptionContainAnyReferenceToTogetherWithRightsOfWayOrEasements1:
		'Does the legal description contain any reference to together with rights of way or easements? 1',
	ifYesPleaseProvideTheDetails: 'If Yes, please provide the details',
	doesAPhysicalInspectionOfThePropertyRevealPersonsInPossessionOrUsingAnyPortionOfTheLandEGUnregisteredLeaseOrDrivewaysServicingOtherLands:
		'Does a physical inspection of the property reveal persons in possession or using any portion of the land? (e.g. unregistered lease or driveways servicing other lands)',
	ifYesPleaseProvideTheDetails1: 'If Yes, please provide the details 1',
	noAgentPleaseSendACopyOfTheAgreementOfPurchaseAndSaleAndTitle:
		'No Agent (please send a copy of the Agreement of Purchase and Sale and title.)',
	wasAnyPortionOfTheDepositPaidDirectlyToTheVendorDoNotAnswerYesIfTheDepositWasPaidToTheVendorsSolicitorAndIsRetainedInHisTrustAccount:
		'Was any portion of the deposit paid directly to the vendor? (Do not answer Yes if the deposit was paid to the vendors solicitor and is retained in his trust account)',
	haveThereBeenAnyAmendmentsWithRespectToThePurchasePriceAndOrDepositAfterTheDateOfSigningTheAgreementOfPurchaseAndSaleWhichExceedTheSumOf3000000:
		'Have there been any Amendments with respect to the purchase price and/or deposit after the date of signing the Agreement of Purchase and Sale, which exceed the sum of $30,000.00?',
	isThereAMortgageOnTitleThatWillBePaidOutWithTheSaleProceeds:
		'Is there a mortgage on title that will be paid out with the sale proceeds?',
	ifTheMortgageOnTitleIsHeldByAPrivateLenderWillYouHaveTheDischargeAvailableOnClosing:
		'If the mortgage on title is held by a private lender will you have the discharge available on closing?',
	haveThereBeenAnyTransfersOfTitleConveyancesOrDischargesOfMortgagesRegisteredWithinTheLast6Months:
		'Have there been any transfers of title/conveyances or discharges of mortgages registered within the last 6 months?',
	areAnyVendorsSigningByWayOfPowerOfAttorney:
		'Are any Vendors signing by way of Power of Attorney?',
	ifThePropertyIsACondoDoesTheStatusCertificateIndicateAnyAdverseMattersThatWillNotBeClearOnClosing:
		'If the property is a condo, does the status certificate indicate any adverse matters that will not be clear on closing?',
	areAnyOfTheBorrowersSigningByWayOfPowerOfAttorney:
		'Are any of the borrowers signing by way of Power of Attorney?',
	willAPortionOfTheProceedsBeUsedToPayOutAllExistingMortgages:
		'Will a portion of the proceeds be used to pay out all existing mortgages?',
	willTaxesBePaidUpToDateOnClosing: 'Will taxes be paid up to date on closing?',
	toTheExtentUtilitiesFormALienWillTheyBePaidUpToDateOnClosing:
		'To the extent utilities form a lien, will they be paid up to date on closing?',
	areYouGettingADeclarationOfPossessionOrStatutoryDeclaration:
		'Are you getting a Declaration of Possession or Statutory Declaration?',
	haveAnyTransfersOfTitleConveyancesOrDischargesOfMortgageBeenRegisteredWithinTheLast6MonthsIncludingTransfersOfTitleRegisteredImmediatelyPriorToThisMortgage:
		'Have any transfers of title/conveyances or discharges of mortgage been registered within the last 6 months, including transfers of title registered immediately prior to this mortgage?',
	ifThePropertyIsACondoDoesTheStatusCertificateIndicateAnyAdverseMatters:
		'If the property is a condo, does the status certificate indicate any adverse matters?',
	companyAgentName: 'Company/Agent Name:',
	telephoneNumberAreaCode2: 'Telephone Number Area Code 2',
	telephoneNumber2: 'Telephone Number 2',
	areThereAnyUnregisteredCommercialLeasesOrAgreementsToLease:
		'Are there any unregistered commercial leases or agreements to lease?',
	instrumentTypeRow1: 'Instrument Type Row 1',
	instrumentNumberRow1: 'Instrument Number Row 1',
	registrationDateMmDdYyyyRow1: 'Registration Date MM/DD/YYYY Row 1',
	instrumentTypeRow2: 'Instrument Type Row 2',
	instrumentNumberRow2: 'Instrument Number Row 2',
	registrationDateMmDdYyyyRow2: 'Registration Date MM/DD/YYYY Row 2',
	instrumentTypeRow3: 'Instrument Type Row 3',
	instrumentNumberRow3: 'Instrument Number Row 3',
	registrationDateMmDdYyyyRow3: 'Registration Date MM/DD/YYYY Row 3',
	haveAllAgreementsRestrictiveCovenantsAndConditionsBeenCompliedWith:
		'Have all agreements, restrictive covenants and conditions been complied with?',
	ifNoPleaseProvideDetails1: 'If no, please provide details: 1',
	listDetailsOfAnyOtherMattersThatWouldNormallyQualifyYourOpinionIncludingButNotLimitedToTitleMattersJudgementsLiensEtcLine1:
		'List details of any other matters that would normally qualify your opinion (including but not limited to title matters, judgements, liens, etc.): Line 1',
	listDetailsOfAnyOtherMattersThatWouldNormallyQualifyYourOpinionIncludingButNotLimitedToTitleMattersJudgementsLiensEtcLine2:
		'List details of any other matters that would normally qualify your opinion (including but not limited to title matters, judgements, liens, etc.): Line 2',
	doYouConfirmTitleIsAsYouHaveAdvisedOnThisOrder:
		'Do you confirm title is as you have advised on this order?',
	ifASurveyOrReferencePlanIsAvailableDoesItRevealAnyDefects:
		'If a survey or reference plan is available, does it reveal any defects?',
	ifNoPleaseProvideDetails2Line1: 'If no, please provide details: 2 Line 1',
	ifNoPleaseProvideDetails2Line2: 'If no, please provide details: 2 Line 2',
	areTheNetMortgageProceedsAfterPaymentsToAnySecuredOrUnsecuredCreditorForWhichThereIsEvidenceOfADebtBeingPaidByLendersBorrowersOrVendorsCounselToAllRegisteredOwners:
		'Are the net mortgage proceeds (after payments to any secured or unsecured creditor for which there is evidence of a debt) being paid by lenders borrowers or vendors counsel to all registered owners?',
	nameOfVendorsSolicitorFirmAndLawyerName:
		'Name of Vendors Solicitor (Firm and Lawyer Name):',
	phoneNumber: 'Phone number:',
	mayWeContactAnyAdditionalLawyerNotaryInvolvedInThisTransaction:
		'May we contact any additional lawyer/notary involved in this transaction?',
	areTheNetMortgageProceedsAfterPaymentsToAnySecuredOrUnsecuredCreditorForWhichThereIsEvidenceOfADebtBeingPaidByLendersOrBorrowersCounselToAllRegisteredOwners:
		'Are the net mortgage proceeds (after payments to any secured or unsecured creditor for which there is evidence of a debt) being paid by lenders or borrowers counsel to all registered owners?',
	ifTheMortgageOnTitleIsHeldByAPrivateLenderWillYouHaveTheDischargeAvailableOnClosing1:
		'If the mortgage on title is held by a private lender will you have the discharge available on closing? 1',
	loanPolicy: 'Loan Policy',
	ownersPolicy: 'Owners Policy',
	purchaserBorrower: 'Purchaser/Borrower',
	purchaserBorrower1: 'Purchaser/Borrower_1',
	lender: 'Lender',
	lender1: 'Lender_1',
	constructionAdvances: 'Construction Advances',
	subsequentAdvancesUpToAmountOfInsurance:
		'Subsequent Advances up to Amount of Insurance',
	subsequentAdvancesExceedingAmountOfInsurance:
		'Subsequent Advances exceeding Amount of Insurance',
	revolvingCreditFacility: 'Revolving Credit Facility',
	variableRateOfInterest: 'Variable Rate of Interest',
	purchaserAddressForService: 'Purchaser Address for Service:',
	mortgageeAddressForService: 'Mortgagee Address for service:',
};

export interface FCT_OVER_25 {
	transactionInformation: string;
	interestHeld: string;
	propertyType: string;
	hasAnOrderForThisTransactionPreviouslyBeenPlacedWithAnotherTitleInsurer: string;
	yourFileNumber: string;
	date: string;
	solicitorNotary: string;
	contact: string;
	lawFirm: string;
	addressNewCustomersOnly: string;
	emailAddress: string;
	telephoneNumberAreaCode: string;
	telephoneNumber: string;
	faxNumberAreaCode: string;
	faxNumber: string;
	solicitorNotary1: string;
	contact1: string;
	lawFirm1: string;
	address: string;
	emailAddress1: string;
	telephoneNumberAreaCode1: string;
	telephoneNumber1: string;
	faxNumberAreaCode1: string;
	faxNumber1: string;
	fctReferenceNumber: string;
	closingDateMmDdYyyy: string;
	purchasePrice: string;
	numberOfUnitsIfCmhcInsured: string;
	otherPleaseSpecify: string;
	ifLeaseholdPleaseProvideNameOfLandlordAndLeaseRegistrationParticulars: string;
	pleaseProvideTermOfLease: string;
	municipalAddressLine1: string;
	municipalAddressLine2: string;
	legalVehicularAndPedestrianAccessToThePropertyIsVia: string;
	legalDescriptionLine1: string;
	legalDescriptionLine2: string;
	isThePropertyContiguous: string;
	doesTheLegalDescriptionDescribeTheSamePropertyAsThatIdentifiedByTheAssessmentRollNumber: string;
	municipalAddress1Line1: string;
	municipalAddress1Line2: string;
	legalDescription1Line1: string;
	legalDescription1Line2: string;
	purchaserMortgagorName: string;
	beneficialOwnerIfApplicable: string;
	hasTheBeneficialOwnerConsentedToThisTransaction: string;
	areYouActingForThePurchaserMortgagor: string;
	ifMortgagorIsACorporationHasANoticeOfChangeBeenFiledWithinThePast12MonthsThatChangesTheOfficersDirectorsOrShareholders: string;
	mortgagee: string;
	custodianIfApplicable: string;
	mortgageAmount: string;
	insuredAmount: string;
	mortgageReferenceNumber: string;
	priority: string;
	other: string;
	wouldYouLikeToAddTheExtendedSuperPriorityLiensCoverageAdditionalPremiumApplies: string;
	wouldYouLikeToAddThePostPolicyDateEncroachmentsRestrictionsWorkOrdersCoverageAdditionalPremiumApplies: string;
	pin: string;
	pin1: string;
	legalDescription: string;
	legalVehicularAndPedestrianAccessToThePropertyIsVia1: string;
	unknown: string;
	unknown1: string;
	legalDescription1: string;
	isThePropertyContiguous1: string;
	ifNoPleaseProvideDetails: string;
	doesTheLegalDescriptionDescribeTheSamePropertyAsThatIdentifiedByTheAssessmentRollNumber1: string;
	doesTheLegalDescriptionContainAnyReferenceToTogetherWithRightsOfWayOrEasements: string;
	doesTheLegalDescriptionContainAnyReferenceToTogetherWithRightsOfWayOrEasements1: string;
	ifYesPleaseProvideTheDetails: string;
	doesAPhysicalInspectionOfThePropertyRevealPersonsInPossessionOrUsingAnyPortionOfTheLandEGUnregisteredLeaseOrDrivewaysServicingOtherLands: string;
	ifYesPleaseProvideTheDetails1: string;
	noAgentPleaseSendACopyOfTheAgreementOfPurchaseAndSaleAndTitle: string;
	wasAnyPortionOfTheDepositPaidDirectlyToTheVendorDoNotAnswerYesIfTheDepositWasPaidToTheVendorsSolicitorAndIsRetainedInHisTrustAccount: string;
	haveThereBeenAnyAmendmentsWithRespectToThePurchasePriceAndOrDepositAfterTheDateOfSigningTheAgreementOfPurchaseAndSaleWhichExceedTheSumOf3000000: string;
	isThereAMortgageOnTitleThatWillBePaidOutWithTheSaleProceeds: string;
	ifTheMortgageOnTitleIsHeldByAPrivateLenderWillYouHaveTheDischargeAvailableOnClosing: string;
	haveThereBeenAnyTransfersOfTitleConveyancesOrDischargesOfMortgagesRegisteredWithinTheLast6Months: string;
	areAnyVendorsSigningByWayOfPowerOfAttorney: string;
	ifThePropertyIsACondoDoesTheStatusCertificateIndicateAnyAdverseMattersThatWillNotBeClearOnClosing: string;
	areAnyOfTheBorrowersSigningByWayOfPowerOfAttorney: string;
	willAPortionOfTheProceedsBeUsedToPayOutAllExistingMortgages: string;
	willTaxesBePaidUpToDateOnClosing: string;
	toTheExtentUtilitiesFormALienWillTheyBePaidUpToDateOnClosing: string;
	areYouGettingADeclarationOfPossessionOrStatutoryDeclaration: string;
	haveAnyTransfersOfTitleConveyancesOrDischargesOfMortgageBeenRegisteredWithinTheLast6MonthsIncludingTransfersOfTitleRegisteredImmediatelyPriorToThisMortgage: string;
	ifThePropertyIsACondoDoesTheStatusCertificateIndicateAnyAdverseMatters: string;
	companyAgentName: string;
	telephoneNumberAreaCode2: string;
	telephoneNumber2: string;
	areThereAnyUnregisteredCommercialLeasesOrAgreementsToLease: string;
	instrumentTypeRow1: string;
	instrumentNumberRow1: string;
	registrationDateMmDdYyyyRow1: string;
	instrumentTypeRow2: string;
	instrumentNumberRow2: string;
	registrationDateMmDdYyyyRow2: string;
	instrumentTypeRow3: string;
	instrumentNumberRow3: string;
	registrationDateMmDdYyyyRow3: string;
	haveAllAgreementsRestrictiveCovenantsAndConditionsBeenCompliedWith: string;
	ifNoPleaseProvideDetails1: string;
	listDetailsOfAnyOtherMattersThatWouldNormallyQualifyYourOpinionIncludingButNotLimitedToTitleMattersJudgementsLiensEtcLine1: string;
	listDetailsOfAnyOtherMattersThatWouldNormallyQualifyYourOpinionIncludingButNotLimitedToTitleMattersJudgementsLiensEtcLine2: string;
	doYouConfirmTitleIsAsYouHaveAdvisedOnThisOrder: string;
	ifASurveyOrReferencePlanIsAvailableDoesItRevealAnyDefects: string;
	ifNoPleaseProvideDetails2Line1: string;
	ifNoPleaseProvideDetails2Line2: string;
	areTheNetMortgageProceedsAfterPaymentsToAnySecuredOrUnsecuredCreditorForWhichThereIsEvidenceOfADebtBeingPaidByLendersBorrowersOrVendorsCounselToAllRegisteredOwners: string;
	nameOfVendorsSolicitorFirmAndLawyerName: string;
	phoneNumber: string;
	mayWeContactAnyAdditionalLawyerNotaryInvolvedInThisTransaction: string;
	areTheNetMortgageProceedsAfterPaymentsToAnySecuredOrUnsecuredCreditorForWhichThereIsEvidenceOfADebtBeingPaidByLendersOrBorrowersCounselToAllRegisteredOwners: string;
	ifTheMortgageOnTitleIsHeldByAPrivateLenderWillYouHaveTheDischargeAvailableOnClosing1: string;
	loanPolicy: string;
	ownersPolicy: string;
	purchaserBorrower: string;
	purchaserBorrower1: string;
	lender: string;
	lender1: string;
	constructionAdvances: string;
	subsequentAdvancesUpToAmountOfInsurance: string;
	subsequentAdvancesExceedingAmountOfInsurance: string;
	revolvingCreditFacility: string;
	variableRateOfInterest: string;
	purchaserAddressForService: string;
	mortgageeAddressForService: string;
}
